import React, { useState } from 'react';

import InputAdornment from '@material-ui/core/InputAdornment';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';

import { normalizePhone } from '../businesslogic/form-normalizers';
import { validatePhone } from '../businesslogic/form-validators';

import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

type InputProps = {
  // Our configurations
  includeAdornment?: boolean;
  customAdornment?: { startAdornment: JSX.Element };
  onError?: (error: string | null) => void;

  // Configurations that are on both, but that we override
  value: string | null;
  onChange: (phoneNumber: string | null) => void;

  // Added by React
  innerRef?: any;
};

type Props = InputProps & Omit<TextFieldProps, keyof InputProps>;

export const UiPhoneTextFieldWithReference = React.forwardRef<any, Props>((props, ref) => (
  <UiPhoneTextField innerRef={ref} {...props} />
));

export const UiPhoneTextField = ({
  label,
  required,
  value,
  onChange,
  onError,
  includeAdornment,
  customAdornment,
  error,
  helperText,
  ...textFieldProps
}: Props) => {
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const inputProps =
    customAdornment ||
    (includeAdornment
      ? {
          startAdornment: (
            <InputAdornment position="start">
              <MaterialDesignIcon iconName={ICON_NAMES.CALL} />
            </InputAdornment>
          ),
        }
      : undefined);

  let displayValue = value ?? '';
  const e164Test = /^\+1([\d]{10})$/g.exec(displayValue);
  if (e164Test) {
    displayValue = e164Test[1] ?? '';
  }
  displayValue = normalizePhone(displayValue) ?? '';

  const onBlurHandler = () => {
    if (!required && !value) {
      setPhoneError(null);
      if (onError) {
        onError(null);
      }
    }
  };

  const phoneNumberOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhoneNumber = e.target.value;

    const numbersInValue = newPhoneNumber.replace(/[^\d]/g, '');
    if (numbersInValue.length > 10) {
      return;
    }

    const normalizedPhone = normalizePhone(newPhoneNumber);
    onChange(normalizedPhone);

    if (setPhoneError) {
      const phoneErrorTest = validatePhone(normalizedPhone);
      if (phoneErrorTest) {
        setPhoneError(phoneErrorTest);
        if (onError) {
          onError(phoneErrorTest);
        }
      } else {
        setPhoneError(null);
        if (onError) {
          onError(null);
        }
      }
    }
  };

  return (
    <TextField
      value={displayValue}
      onChange={phoneNumberOnChangeHandler}
      onBlur={onBlurHandler}
      error={Boolean(phoneError) || error}
      helperText={phoneError || helperText}
      // Passthrough options
      label={label ?? 'Phone Number'}
      InputProps={inputProps}
      required={required}
      {...textFieldProps}
    />
  );
};
